import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Root as AccordionRoot } from '@radix-ui/react-accordion';
import { Content, Item, Trigger } from '@radix-ui/react-accordion';
import { faAngleDown } from '@soundxyz/font-awesome/pro-solid-svg-icons/index.mjs';
import { keyframes, styled, theme } from '@components-shared/src/stitches';
import { Button } from '@components/main-components/button/Button';
import { StandardModal } from '@components/main-components/modals/StandardModal';
import { ModalContext } from '@context/modal';

export function FaqModal({
  faq,
  title,
  learnMoreUrl,
}: {
  faq: Record<string, JSX.Element>;
  title: string;
  learnMoreUrl: string;
}) {
  const { closeCancelModal } = ModalContext.useContainer();

  const onBackClick = () => {
    closeCancelModal();
  };

  const onLearnMoreClick = () => {
    const newWindow = window.open(learnMoreUrl, '_blank', 'noopener,noreferrer');
    if (newWindow) newWindow.opener = null;
  };

  const body = (
    <>
      <StyledAccordion type="single" collapsible>
        {Object.entries(faq).map(([q, a], i) => (
          <StyledItem key={q} value={q}>
            <StyledTrigger>
              <Number>{i + 1}</Number>
              <Question>{q}</Question>
              <IconWrapper>
                <FontAwesomeIcon icon={faAngleDown} color={theme.colors.base500.value} />
              </IconWrapper>
            </StyledTrigger>
            <Answer>
              {a}
              {/* Use breaks to add padding
              This avoids Framer Motion from jumping at the end of open close animation */}
              <br />
              <br />
            </Answer>
          </StyledItem>
        ))}
      </StyledAccordion>
    </>
  );

  return (
    <StandardModal
      body={body}
      title={title}
      closeModal={closeCancelModal}
      backCallToAction={
        <Button
          variant="secondary"
          label="Learn more"
          onClick={onLearnMoreClick}
          size="M"
          fullWidth
        />
      }
      callToAction={
        <Button variant="primary" label="Close" onClick={onBackClick} size="M" fullWidth />
      }
      bodyWithoutPaddingBottom
    />
  );
}

const StyledAccordion = styled(AccordionRoot, {
  height: 'inherit',
  display: 'flex',
  flexDirection: 'column',
});

export const Number = styled('div', {
  base: 'S',
  color: '$white',
  background: '$black',
  borderRadius: '$full',
  marginRight: 8,
  flexShrink: 0,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: 24,
  height: 24,
  fontWeight: 800,
});

export const StyledItem = styled(Item, {
  display: 'flex',
  flexDirection: 'column',
  gap: 0,
  borderBottom: '1px solid $base300',

  '&:last-child': {
    borderBottom: 'none',
  },
});

export const IconWrapper = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: 24,
  height: 24,
  marginLeft: 'auto',
  flexShrink: 0,
});

const slideDown = keyframes({
  '0%': { height: 0 },
  '100%': { height: 'var(--radix-accordion-content-height)' },
});

const slideUp = keyframes({
  '0%': { height: 'var(--radix-accordion-content-height)' },
  '100%': { height: 0 },
});

const StyledTrigger = styled(Trigger, {
  display: 'flex',
  width: '100%',
  justifyContent: 'flex-start',
  alignItems: 'center',

  svg: {
    transition: 'transform 300ms',
  },

  "&[data-state='open']": {
    svg: {
      transform: 'rotate(180deg)',
    },
  },
});

const Question = styled('h3', {
  title: 'XS',
  textAlign: 'left',
  marginRight: 16,
  paddingY: 16,
});

const Answer = styled(Content, {
  base: 'M',
  color: '$base500',
  overflow: 'hidden',
  paddingX: 32,

  "&[data-state='open']": {
    animation: `${slideDown} 300ms ease-out`,
  },
  "&[data-state='closed']": {
    animation: `${slideUp} 300ms ease-out`,
  },
});
